<script>
	import { signOutUser } from '@ctm/auth';
	import { signedInUser } from '@ctm/auth/store';
	import { HeaderBar, Modal, Stack, Text, UserMenu } from '@ctm/compass';
	import { Icon } from '@ctm/icons';
	import { Document } from '@ctm/icons/data';
	import { Email, Lock, Logout, Phone } from '@ctm/icons/interface';
	import { Return } from '@ctm/icons/travel';
	import { Portal as PortalLogo } from '@ctm/icons/products';
	import { page } from '$app/stores';
	import BeamerTrigger from '$lib/components/Beamer/BeamerTrigger.svelte';
	import { debtorSettings, userDebtors, userAccounts } from '$lib/stores/settings';

	import styles from './Nav.module.css';

	const primaryDebtor = $signedInUser.applicationEntities ? $signedInUser.applicationEntities[0] : null;
	function getDebtorToggleUrl(debtorCode) {
		const impersonationToken = $page.data.impersonationToken;
		const params = new URLSearchParams();
		const path = '/dashboard';

		if (debtorCode !== primaryDebtor) {
			if (impersonationToken) {
				params.set('d', `${debtorCode}|${impersonationToken}`);
			} else {
				params.set('d', debtorCode);
			}
		} else if (impersonationToken) {
			params.set('d', `|${impersonationToken}`);
		}

		const queryString = params.toString();
		return queryString ? `${path}?${queryString}` : path;
	}

	$: account = $signedInUser.debtor;
	function getAccountOptions(userDebtors, userAccounts) {
		if (userAccounts.length > 1) {
			return userAccounts.map((account) => ({
				'data-sveltekit-reload': true,
				href: account.type === 'Debtor' ? getDebtorToggleUrl(account.data.debtorCode) : account.data.url,
				label: account.name,
				description: account.region,
				value: account.type === 'Debtor' ? account.data.debtorCode : account.data.url,
			}));
		}
		if (userDebtors.length > 1) {
			return userDebtors.map(({ debtorCode, debtorName }) => ({
				'data-sveltekit-reload': true,
				href: getDebtorToggleUrl(account.data.debtorCode),
				label: debtorName,
				description: 'Australia & New Zealand',
				value: debtorCode,
			}));
		}
		return [];
	}

	/** @type {Modal} */
	let contactUsModal;
	function handleContactUs() {
		contactUsModal.show();
	}

	function handleLogout() {
		signOutUser();
	}

	function handleClose() {
		// this will only work if opened from another tab/window
		if (window.opener) {
			window.close();
		}
	}

	$: hasSupportDetails = $debtorSettings.supportPhoneNumber || $debtorSettings.supportEmailAddress;

	$: accountOptions = getAccountOptions($userDebtors, $userAccounts);
</script>

<HeaderBar>
	<Icon icon={PortalLogo} slot="logo" />
	<svelte:fragment slot="right">
		{#if $debtorSettings.beamerEnabled}
			<BeamerTrigger />
		{/if}
		<UserMenu user={$signedInUser}>
			{#if accountOptions.length > 1}
				<UserMenu.Item icon={Return} label="Switch accounts">
					{#each accountOptions as option}
						<UserMenu.Item {...option} checked={account === option.value} type="radio" />
					{/each}
				</UserMenu.Item>
			{/if}
			<UserMenu.Item icon={Document} label="Terms & conditions" href="https://www.travelctm.com/terms-conditions/" target="_blank" rel="noreferrer" />
			<UserMenu.Item icon={Lock} label="Privacy policy" href="https://www.travelctm.com/global-privacy-policy/" target="_blank" rel="noreferrer" />
			{#if hasSupportDetails}
				<UserMenu.Item icon={Phone} label="Contact us" onclick={handleContactUs} />
			{/if}
			{#if $page.data.impersonationToken}
				<UserMenu.Item icon={Logout} label="Close" onclick={handleClose} />
			{:else}
				<UserMenu.Item icon={Logout} label="Logout" onclick={handleLogout} />
			{/if}
		</UserMenu>
	</svelte:fragment>
</HeaderBar>

{#if hasSupportDetails}
	<Modal bind:this={contactUsModal} title="Need help?">
		<Text>Your CTM travel team:</Text>
		<Stack gap="xs">
			{#if $debtorSettings.supportPhoneNumber}
				<a class={styles['contact-link']} href="tel:{$debtorSettings.supportPhoneNumber}">
					<Icon class={styles['contact-link-icon']} icon={Phone} size="md" />
					<span>{$debtorSettings.supportPhoneNumber}</span>
				</a>
			{/if}
			{#if $debtorSettings.supportEmailAddress}
				<a class={styles['contact-link']} href="mailto:{$debtorSettings.supportEmailAddress}">
					<Icon class={styles['contact-link-icon']} icon={Email} size="md" />
					<span>{$debtorSettings.supportEmailAddress}</span>
				</a>
			{/if}
		</Stack>
		<Text tone="subtle">Contact us anytime for support. Available 24/7; after-hours fees may apply.</Text>
	</Modal>
{/if}

<style>
	:global(header[class^='_header']) {
		grid-template-columns: 320px 1fr minmax(320px, 380px) !important;
	}
</style>
